/* eslint-disable prettier/prettier */
import React, { useState } from "react";
import useFontScale from "../../hooks/useFontScale";
import { Typography } from "@mui/material";
import { pageLocale } from "../../utils/locale";
import {
  getParams,
  getMetricReading,
  formatReportGender,
} from "../../utils/helpers";
import {
  attachSeverity,
  dateFormater,
  severityForType,
} from "../../utils/commonfunction";
import { normalizeSeverity } from "../../utils/pdfReportHelpers";
import { getTugCategoryConfig } from "../../utils/metricsConfig";
import "../tugSide/v2/summary/tugsidemetricsdetailsv2.scss";
import vectorIcon from "../../assets/vector.svg";
import MetricDetailExpansionV2 from "../tugSide/v2/summary/MetricDetailExpansionV2";
import RiskDefinitionModal from "../tugSide/v2/summary/RiskDefinitionModal";
import StatusMetricBlock from "../tugSide/v2/summary/StatusMetricBlock";
import ExerciseRecommendations from "../tugFront/summary/ExerciseRecommendations";
import { SIDE_TUG_METRIC_TREND } from "../../routes/routerPath";

const hasValidMetricValue = (v) => {
  if (v === null || v === undefined || v === "") return false;
  return Number.isFinite(Number(v));
};

const TugMetricsDetailsV2 = ({
  reportData,
  userData,
  setOpenErrorAlert,
  reportType = "side",
  trendPath = SIDE_TUG_METRIC_TREND,
}) => {
  useFontScale();
  const [expandedMetrics, setExpandedMetrics] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [modalCategoryKey, setModalCategoryKey] = useState(null);
  const [modalMetricName, setModalMetricName] = useState(null);

  const data = reportData;
  const user = userData;
  const { locale } = getParams();
  const gender = formatReportGender(user?.gender, locale);

  const mv2 = pageLocale("metricsv2");
  const mv2Categories = pageLocale("metricsv2_categories");
  const mv2Metrics = pageLocale("metricsv2_metrics");
  const mv2Units = pageLocale("metricsv2_units");
  const { na } = pageLocale("common") || { na: "N/A" };

  if (!data) {
    return (
      <div className="tug-side-metrics-details-v2">
        <div style={{ padding: "40px", textAlign: "center" }}>
          <Typography>{mv2?.loading}</Typography>
        </div>
      </div>
    );
  }

  const rawCategories = getTugCategoryConfig(reportType);
  const categories = rawCategories.map((cat) => ({
    ...cat,
    name: mv2Categories?.[cat.key],
  }));

  const CATEGORY_TO_SUMMARY_KEY = Object.fromEntries(
    rawCategories.map((cat) => [cat.key, cat.summaryKey]),
  );

  const summary = data?.summary || {};

  const severityToRiskLevel = (severity) => {
    if (severity === "good") return { quadrant: 2, isBadToGood: true };
    if (severity === "average") return { quadrant: 1, isBadToGood: true };
    return { quadrant: 0, isBadToGood: true };
  };

  const getRiskLevel = () =>
    severityToRiskLevel(normalizeSeverity(summary.overall));

  const categoryHasData = (categoryKey) => {
    const cat = rawCategories.find((c) => c.key === categoryKey);
    return (
      cat?.metrics.some((m) => hasValidMetricValue(getMetricValue(m))) ?? false
    );
  };

  const getCategoryRiskLevel = (categoryKey) => {
    const severity = normalizeSeverity(
      summary[CATEGORY_TO_SUMMARY_KEY[categoryKey]],
    );
    return { ...severityToRiskLevel(severity), severity: severity || "poor" };
  };

  const getMetricData = (metricConfig) => {
    const { apiField, category } = metricConfig;
    if (!category || !apiField) return null;
    return data?.[category]?.[apiField] ?? null;
  };

  const getMetricValue = (metricConfig) =>
    getMetricReading(getMetricData(metricConfig));

  const formatMetricValue = (value, unit) => {
    if (!hasValidMetricValue(value)) return na || "N/A";
    if (unit === "steps/min" || unit === "count") {
      return `${Math.round(value)}`;
    }
    const num = Number(value);
    if (unit === "percentage (%)") {
      return num % 1 === 0 ? num.toString() : num.toFixed(1);
    }
    if (unit === "unitless") {
      return num % 1 === 0 ? num.toString() : num.toFixed(2);
    }
    return num % 1 === 0 ? num.toString() : num.toFixed(1);
  };

  const getMetricDisplayName = (metricConfig) => {
    const { metric } = metricConfig;
    return mv2Metrics?.[metric] || metric.replace(/_/g, " ");
  };

  const getTrendDirection = (metricConfig) => {
    const raw = getMetricData(metricConfig);
    if (raw && raw.trend) {
      return raw.trend;
    }
    const trendMap = {
      TUG_Time: "improving",
      Sit_to_Stand_Time: "improving",
      Stand_to_Sit_Time: "stable",
      Attempts_to_Rise: "stable",
      Speed: "declining",
      Steps_per_Minute: "improving",
      Step_Length: "declining",
      Foot_Placement: "stable",
      Head_Wobbling: "stable",
      Shoulder_Balance_LRdeg: "stable",
      Hip_Balance_LRdeg: "stable",
      Trunk_Sway: "improving",
      Postural_Control: "stable",
      Right_Left_Step_Symmetry: "improving",
      Gait_Smoothness: "stable",
      Foot_Clearance: "declining",
      Turning_Time: "stable",
      Steps_During_Turn: "improving",
      Joint_Angles_Knee: "stable",
      Joint_Angles_Hip: "improving",
    };
    return trendMap[metricConfig.metric] || "stable";
  };

  const toggleMetric = (categoryName, metricKey) => {
    const key = `${categoryName}-${metricKey}`;
    setExpandedMetrics((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const formatPatientName = () => {
    const firstName = user?.first_name || "";
    const lastName = user?.last_name || "";
    if (!firstName && !lastName) return user?.name || "";
    if (locale === "ja" || locale === "zh") {
      return `${lastName} ${firstName}`.trim();
    }
    return `${firstName} ${lastName}`.trim();
  };

  const getFormattedDate = () => {
    if (data?.timeStamp) {
      return dateFormater(data.timeStamp);
    }
    return "";
  };

  const getRiskLevelText = (riskLevel) => {
    const { quadrant } = riskLevel;
    if (quadrant === 0) return mv2?.risk_high;
    if (quadrant === 1) return mv2?.risk_moderate;
    return mv2?.risk_low;
  };

  const getStatusBadgeColor = (severity) => {
    if (severity === "good") return "#11b981";
    if (severity === "average") return "#f59e0b";
    return "#e53e3e";
  };

  const getStatusText = (severity) => {
    if (severity === "good") return mv2?.status_good;
    if (severity === "average") return mv2?.status_moderate;
    return mv2?.status_poor;
  };

  const normalizeRange = (range) => {
    if (!range || range.length === 0) return range;
    const sorted = [...range].sort((a, b) => a.minMax[0] - b.minMax[0]);
    return sorted.map((r, i) => ({
      ...r,
      minMax: [i === 0 ? 0 : sorted[i - 1].minMax[1], r.minMax[1]],
    }));
  };

  return (
    <div className="tug-side-metrics-details-v2">
      <div className="patient-details-card">
        <div className="patient-image-wrapper">
          {data?.tumbnailPath && (
            <img
              src={data?.tumbnailPath}
              alt="patient"
              className="patient-image"
            />
          )}
        </div>
        <div className="patient-info-wrapper">
          <Typography className="patient-name">
            {formatPatientName()}
          </Typography>
          <Typography className="patient-gender-age">
            {gender}
            {gender && user?.age ? " | " : ""}
            {user?.age ? user.age : ""}
          </Typography>
          <Typography className="patient-date">{getFormattedDate()}</Typography>
          <hr className="divider" />
          <div className="risk-level-container">
            <Typography className="risk-label">
              {mv2?.risk_level_label}
            </Typography>
            <div className="risk-level-badge-wrapper">
              <div
                className={`risk-level-badge ${
                  getRiskLevel().quadrant === 0 ? "high" : ""
                }`}
                style={{
                  backgroundColor:
                    getRiskLevel().quadrant === 0
                      ? "#e53e3e"
                      : getRiskLevel().quadrant === 1
                      ? "#f59e0b"
                      : "#11b981",
                }}
              >
                {(getRiskLevelText(getRiskLevel()) || "").toUpperCase()}
              </div>
            </div>
            <a
              href="#"
              className="define-link"
              onClick={(e) => {
                e.preventDefault();
                setModalType("overall");
                setModalCategoryKey(null);
                setModalMetricName(null);
                setModalOpen(true);
              }}
            >
              &gt; {mv2?.how_its_defined}
            </a>
          </div>
        </div>
      </div>

      {categories.map((category) => {
        const categoryMetrics = category.metrics.map((metric) => {
          const raw = getMetricData(metric);
          const value = getMetricValue(metric);
          const rangeWithSeverity = attachSeverity(raw?.range);
          return {
            ...metric,
            value,
            range: normalizeRange(rangeWithSeverity) || null,
            type: raw?.type || null,
            severity: severityForType(raw?.type, rangeWithSeverity),
          };
        });

        if (categoryMetrics.length === 0) return null;

        const categoryRisk = getCategoryRiskLevel(category.key);
        const catHasData = categoryHasData(category.key);

        return (
          <div key={category.key} className="category-section">
            <StatusMetricBlock
              label={category.name}
              value={
                catHasData
                  ? (getStatusText(categoryRisk.severity) || "").toUpperCase()
                  : (na || "N/A")
              }
              status={catHasData ? categoryRisk.severity : null}
              className="category-status-block"
            />

            <div className="metrics-list">
              {categoryMetrics.map((metric, index) => {
                const metricKey = `${category.key}-${metric.metric}`;
                const isExpanded = expandedMetrics[metricKey];
                const displayName = getMetricDisplayName(metric);
                const trendDirection = getTrendDirection(metric);

                return (
                  <React.Fragment key={metric.metric}>
                    {index > 0 && <div className="metric-divider" />}
                    <div
                      className={`metric-item ${isExpanded ? "expanded" : ""}`}
                    >
                      <div
                        className="metric-header"
                        onClick={() =>
                          toggleMetric(category.key, metric.metric)
                        }
                      >
                        <div className="metric-header-top">
                          <div className="metric-content">
                            <Typography className="metric-name">
                              {displayName}
                            </Typography>
                            <div className="metric-value-wrapper">
                              <Typography className="metric-value">
                                {formatMetricValue(metric.value, metric.unit)}
                              </Typography>
                              <Typography className="metric-unit">
                                {mv2Units?.[metric.metric] ?? ""}
                              </Typography>
                              {trendDirection !== "stable" && (
                                <div
                                  className={`metric-trend ${
                                    trendDirection === "improving"
                                      ? "trend-improving"
                                      : "trend-declining"
                                  }`}
                                >
                                  <span className="trend-arrow">
                                    {trendDirection === "improving" ? "↑" : "↓"}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                          <img
                            src={vectorIcon}
                            className={`metric-arrow ${
                              isExpanded ? "expanded" : ""
                            }`}
                            alt="expand"
                          />
                        </div>

                        <div
                          className={`metric-status-badge-wrapper ${
                            isExpanded ? "expanded" : ""
                          }`}
                        >
                          {metric.severity && (
                            <div
                              className="metric-status-badge"
                              style={{
                                backgroundColor: getStatusBadgeColor(
                                  metric.severity,
                                ),
                              }}
                            >
                              {getStatusText(metric.severity)}
                            </div>
                          )}
                          {isExpanded && (
                            <a
                              href="#"
                              className="define-link"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setModalType("metric");
                                setModalCategoryKey(null);
                                setModalMetricName(displayName);
                                setModalOpen(true);
                              }}
                            >
                              &gt; {mv2?.how_its_calculated}
                            </a>
                          )}
                        </div>
                      </div>

                      {isExpanded && (
                        <MetricDetailExpansionV2
                          metric={metric}
                          value={metric.value}
                          range={metric.range}
                          setOpenErrorAlert={setOpenErrorAlert}
                          trendPath={trendPath}
                        />
                      )}
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        );
      })}

      <div
        className="exercise-recommendation-section"
        style={{ marginTop: "32px", marginBottom: "16px" }}
      >
        <Typography className="metrics-title">
          {mv2?.exercise_recommendation}
        </Typography>
        <Typography
          className="data-desc"
          style={{
            fontFamily: '"OpenSans", sans-serif',
            fontSize: "14px",
            color: "#333",
            marginBottom: "16px",
          }}
        >
          {data?.exerciseRecommended?.programDescription}
        </Typography>
        <ExerciseRecommendations
          exerciseData={data?.exerciseRecommendation || []}
        />
      </div>

      <RiskDefinitionModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        type={modalType}
        categoryKey={modalCategoryKey}
        metricName={modalMetricName}
      />
    </div>
  );
};

export default TugMetricsDetailsV2;
